import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { companiesList } from "@/lib/api";
import { Building2, Search, CheckCircle2, XCircle, GraduationCap, Loader2, CalendarClock, ShieldCheck, ClipboardCheck, Send } from "lucide-react";

/* Companies coming to YOUR campus — only the drives your placement cell has
   added. For each: can you apply, and everything to prepare for it. */

const STEPS = [
  { Icon: ClipboardCheck, t: "Check you're eligible", d: "Each company is checked against your CGPA, branch and score — the same rules the Apply button uses." },
  { Icon: ShieldCheck, t: "Prepare for their exact process", d: "Rounds, syllabus, a 4-week plan and interview questions — plus the proctored mock in their real test pattern." },
  { Icon: Send, t: "Apply from Campus", d: "When you're ready, apply to the drive and track where you stand." },
];

export default function CompaniesPage() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  useEffect(() => { companiesList().then(({ data }) => setData(data)).catch(() => setData({ companies: [], has_college: false })); }, []);

  const rows = useMemo(() => {
    const all = data?.companies || [];
    const n = q.trim().toLowerCase();
    return n ? all.filter((c) => c.name.toLowerCase().includes(n)) : all;
  }, [data, q]);

  if (!data) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading your campus companies…</div>;

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : null);

  return (
    <div className="container-x py-10 md:py-14" data-testid="companies-page">
      <div className="mb-8">
        <span className="overline">Companies at your campus</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">The companies coming for <span className="italic text-stone-500">your</span> placements.</h1>
        <p className="mt-3 text-stone-600 max-w-2xl">Only the companies your placement cell has announced. For each one: whether you can apply, and exactly how to prepare for their process.</p>
      </div>

      {/* How this module works */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        {STEPS.map((s, i) => (
          <div key={i} className="rounded-2xl border border-stone-200 bg-white p-5 flex gap-3">
            <span className="shrink-0 w-9 h-9 rounded-xl bg-amber-50 text-[#C2410C] flex items-center justify-center"><s.Icon size={17} /></span>
            <div><p className="font-medium text-stone-900 text-sm">{i + 1}. {s.t}</p><p className="text-xs text-stone-600 mt-1 leading-relaxed">{s.d}</p></div>
          </div>
        ))}
      </div>

      {!data.has_college ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center">
          <GraduationCap size={30} className="mx-auto text-stone-300" />
          <h2 className="font-display text-2xl mt-4">Join your college first</h2>
          <p className="text-sm text-stone-600 mt-2 max-w-md mx-auto">Companies appear here when your placement cell announces drives. Link your college from the <Link to="/app/campus" className="underline">Campus</Link> page.</p>
        </div>
      ) : data.companies.length === 0 ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center" data-testid="companies-empty">
          <Building2 size={30} className="mx-auto text-stone-300" />
          <h2 className="font-display text-2xl mt-4">No drives announced yet</h2>
          <p className="text-sm text-stone-600 mt-2 max-w-md mx-auto">The moment your placement cell adds a company, its prep hub appears here. Meanwhile, keep your <Link to="/app/coach" className="underline">Coach plan</Link> moving.</p>
        </div>
      ) : (
        <>
          {data.companies.length > 6 && (
            <div className="relative max-w-md mb-6">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a company" data-testid="companies-search"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {rows.map((c) => {
              const ok = c.verdict === "eligible";
              const next = c.drives[0];
              return (
                <Link key={c.slug} to={`/app/companies/${c.slug}`} data-testid={`company-${c.slug}`}
                  className="group rounded-3xl border border-stone-200 bg-white p-6 hover:border-stone-900 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-11 h-11 rounded-xl bg-stone-900 text-amber-300 font-display text-lg flex items-center justify-center shrink-0">{c.name[0]}</span>
                      <div className="min-w-0">
                        <h2 className="font-display text-xl leading-tight truncate">{c.name}</h2>
                        <p className="text-xs text-stone-500">{c.drives.length > 1 ? `${c.drives.length} drives` : next?.title}{next?.role ? ` · ${next.role}` : ""}</p>
                      </div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                      {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {ok ? "You can apply" : "Not eligible"}
                    </span>
                  </div>
                  {!ok && next?.reasons?.length > 0 && <p className="mt-3 text-xs text-stone-600">{next.reasons[0]}</p>}
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
                    {next?.deadline && <span className="inline-flex items-center gap-1"><CalendarClock size={12} className="text-[#C2410C]" /> apply by {fmt(next.deadline)}</span>}
                    {next?.applied && <span className="text-emerald-700 font-medium">Applied ✓</span>}
                    {c.has_pattern && <span className="inline-flex items-center gap-1"><ShieldCheck size={12} /> real test pattern</span>}
                    <span className="ml-auto text-stone-900 font-medium group-hover:text-[#C2410C]">Prepare →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
